import React, { useState } from 'react';
import { X, Sparkles, Loader2 } from 'lucide-react';
import { apiClient } from '../services/api';
import { getLanguage } from '../utils/language';
import { trackAIFolderCreated } from '../utils/analytics';

interface AIFolderGeneratorProps {
  onClose: () => void;
  onSuccess: (newFolderId?: number) => void | Promise<void>;
  categoryId: number;
  parentId: number | null;
  userEmail?: string;
}

export const AIFolderGenerator: React.FC<AIFolderGeneratorProps> = ({
  onClose,
  onSuccess,
  categoryId,
  parentId,
  userEmail,
}) => {
  const [prompt, setPrompt] = useState('');
  const [folderName, setFolderName] = useState('');
  const [cardsOnly, setCardsOnly] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isSubmitDisabled = isGenerating || !prompt.trim();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsGenerating(true);
    setError(null);

    const language = getLanguage(userEmail ?? '');

    try {
      const result = await apiClient.generateAIFolder({
        category_id: categoryId,
        prompt: prompt.trim(),
        language,
        parent_id: parentId,
        ...(folderName.trim() && { folder_name: folderName.trim() }),
        ...(cardsOnly && { cards_only: true }),
      });

      const newFolderId: number | undefined = result?.folder?.id;
      setIsGenerating(false);
      trackAIFolderCreated();
      await Promise.resolve(onSuccess(newFolderId));
      onClose();
    } catch (err: any) {
      const status = err.response?.status;
      if (status === 404) {
        setError('Category not found.');
      } else if (status === 503) {
        setError('AI features are unavailable.');
      } else {
        setError('Failed to generate folder. Please try again.');
      }
      setIsGenerating(false);
    }
  };

  const handleClose = () => {
    if (isGenerating) {
      const confirmed = window.confirm('AI generation is in progress. Are you sure you want to cancel?');
      if (!confirmed) return;
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center space-x-2">
            <Sparkles className="h-6 w-6 text-purple-600" />
            <h2 className="text-xl font-bold text-gray-900">Generate folder with AI</h2>
          </div>
          <button
            onClick={handleClose}
            disabled={isGenerating}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-50"
            title="Close"
          >
            <X className="h-5 w-5 text-gray-600" />
          </button>
        </div>

        {/* Loading Overlay */}
        {isGenerating && (
          <div className="absolute inset-0 bg-white bg-opacity-95 flex flex-col items-center justify-center z-10 rounded-lg">
            <Loader2 className="h-12 w-12 text-purple-600 animate-spin mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              AI is generating your folder...
            </h3>
            <p className="text-gray-600 text-center max-w-sm">
              This may take a few seconds. Our AI is creating a folder with vocabulary cards based on your input.
            </p>
            <div className="mt-6 flex space-x-2">
              <div className="w-3 h-3 bg-purple-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
              <div className="w-3 h-3 bg-purple-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
              <div className="w-3 h-3 bg-purple-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Prompt <span className="text-red-500">*</span>
              </label>
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="e.g., Gym equipment — include sections for free weights, machines, and accessories"
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                disabled={isGenerating}
                autoFocus
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Folder name{' '}
                <span className="text-gray-400 font-normal">(optional — inferred from prompt if blank)</span>
              </label>
              <input
                type="text"
                value={folderName}
                onChange={(e) => setFolderName(e.target.value)}
                placeholder="e.g., Gym Equipment"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                disabled={isGenerating}
              />
            </div>

            <label className="flex items-center gap-2 cursor-pointer select-none w-fit">
              <input
                type="checkbox"
                checked={cardsOnly}
                onChange={(e) => setCardsOnly(e.target.checked)}
                disabled={isGenerating}
                className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
              />
              <span className="text-sm text-gray-700">Cards only (no subfolders)</span>
            </label>
          </form>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50 flex-shrink-0">
          <button
            type="button"
            onClick={handleClose}
            disabled={isGenerating}
            className="px-5 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
            className="px-5 py-2.5 text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
          >
            <Sparkles className="h-4 w-4" />
            <span>{isGenerating ? 'Generating...' : 'Generate folder'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};
